import React, { useState } from 'react';
import { getProductStock, formatStockInfo, getStockSummary } from '../../services/stockService';
import './StockQuery.css';

const StockQuery = () => {
    const [itemNo, setItemNo] = useState('');
    const [loading, setLoading] = useState(false);
    const [stockData, setStockData] = useState(null);
    const [error, setError] = useState('');
    const [summary, setSummary] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!itemNo.trim()) {
            setError('Por favor, ingresa el número de artículo');
            return;
        }

        setLoading(true);
        setError('');
        setStockData(null);
        setSummary(null);

        try {
            const result = await getProductStock(itemNo.trim());

            if (result.success) {
                setStockData(result.data);
                setSummary(getStockSummary(result.data));
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError(err.message || 'Error al consultar el stock');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setItemNo('');
        setStockData(null);
        setError('');
        setSummary(null);
    };

    const formatQuantity = (quantity) => {
        return new Intl.NumberFormat('es-ES', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(quantity);
    };

    const getStockStatusClass = (quantity) => {
        if (quantity > 0) return 'stock-positive';
        if (quantity < 0) return 'stock-negative';
        return 'stock-zero';
    };

    return (
        <div className="stock-query-container">
            <div className="stock-query-header">
                <h2>📦 Consulta de Stock</h2>
                <p>Consulta el stock de un producto en todas las ubicaciones</p>
            </div>

            <form onSubmit={handleSubmit} className="stock-query-form">
                <div className="form-group">
                    <label htmlFor="itemNo">Número de Artículo:</label>
                    <div className="input-group">
                        <input
                            type="text"
                            id="itemNo"
                            value={itemNo}
                            onChange={(e) => setItemNo(e.target.value)}
                            placeholder="Ej: 1896-S"
                            disabled={loading}
                            className="item-input"
                        />
                        <button
                            type="submit"
                            disabled={loading || !itemNo.trim()}
                            className="search-button"
                        >
                            {loading ? '🔄' : '🔍'} {loading ? 'Consultando...' : 'Consultar'}
                        </button>
                        {(stockData || error) && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="clear-button"
                            >
                                🗑️ Limpiar
                            </button>
                        )}
                    </div>
                </div>
            </form>

            {error && (
                <div className="error-message">
                    ⚠️ {error}
                </div>
            )}

            {summary && (
                <div className="stock-summary">
                    <h3>📊 Resumen del Producto</h3>
                    <div className="summary-grid">
                        <div className="summary-item">
                            <span className="summary-label">Producto:</span>
                            <span className="summary-value">{summary.productInfo?.itemNo}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Descripción:</span>
                            <span className="summary-value">{summary.productInfo?.description}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Categoría:</span>
                            <span className="summary-value">{summary.productInfo?.category}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Unidad:</span>
                            <span className="summary-value">{summary.productInfo?.unitOfMeasure}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Stock Total:</span>
                            <span className={`summary-value ${getStockStatusClass(summary.totalStock)}`}>
                                {formatQuantity(summary.totalStock)}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Ubicaciones:</span>
                            <span className="summary-value">{summary.totalLocations}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Con Stock:</span>
                            <span className="summary-value stock-positive">{summary.locationsWithStock}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Stock Negativo:</span>
                            <span className="summary-value stock-negative">{summary.locationsWithNegativeStock}</span>
                        </div>
                    </div>
                </div>
            )}

            {stockData && stockData.productStock && (
                <div className="stock-results">
                    <h3>📍 Stock por Ubicación</h3>
                    <div className="stock-table-container">
                        <table className="stock-table">
                            <thead>
                                <tr>
                                    <th>Ubicación</th>
                                    <th>Nombre</th>
                                    <th>Ciudad</th>
                                    <th>Stock</th>
                                    <th>Contacto</th>
                                    <th>Última Actualización</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stockData.productStock.map((item, index) => {
                                    const formattedItem = formatStockInfo(item);
                                    return (
                                        <tr key={index} className={`stock-row ${getStockStatusClass(formattedItem.stockQuantity)}`}>
                                            <td className="location-code">
                                                {formattedItem.locationCode}
                                            </td>
                                            <td className="location-name">
                                                {formattedItem.locationName}
                                                {formattedItem.locationAddress && (
                                                    <div className="location-address">
                                                        {formattedItem.locationAddress}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="location-city">
                                                {formattedItem.locationCity}
                                            </td>
                                            <td className={`stock-quantity ${getStockStatusClass(formattedItem.stockQuantity)}`}>
                                                <span className="quantity-value">
                                                    {formatQuantity(formattedItem.stockQuantity)}
                                                </span>
                                                <span className="quantity-unit">
                                                    {formattedItem.unitOfMeasure}
                                                </span>
                                            </td>
                                            <td className="location-contact">
                                                {formattedItem.locationContact}
                                            </td>
                                            <td className="last-update">
                                                {formattedItem.lastUpdateDate}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="query-info">
                        <p>
                            <strong>Fecha de consulta:</strong> {stockData.queryDate} |
                            <strong> Total de registros:</strong> {stockData.totalRecords}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockQuery; 